import { useState, useReducer, useEffect, useMemo } from "react";
import { useMutation } from "@tanstack/react-query";
import { v4 as uuidv4 } from "uuid";
import useAuthContext from "./useAuthContext";
import { sendFeedback } from "../api/feedback";
import { FeedbackAction, FeedbackState } from "../types/feedback";

function feedbackReducer(state: FeedbackState, action: FeedbackAction): FeedbackState {
  switch (action.type) {
    case "SET_FEEDBACK_TYPE":
      return { ...state, feedbackType: action.payload };
    case "SET_COMMENT":
      return { ...state, comment: action.payload };
    case "SET_TIMESTAMP":
      return { ...state, timeStamp: action.payload };
    case "RESET":
      return action.payload;
    default:
      return state;
  }
}

const buildInitialState = (tid: string, oid: string, name: string): FeedbackState => ({
  partitionKey: tid,
  rowKey: uuidv4(),
  timeStamp: "",
  userId: oid,
  displayName: name,
  feedbackType: "Like",
  comment: "",
});

export const useFeedback = ({ onFormSubmit, setIsOpenFeedback }) => {
  const { tokenData } = useAuthContext();
  const { tid, name, oid } = tokenData;

  const initialState = useMemo<FeedbackState>(
    () => buildInitialState(tid, oid, name),
    [tid, oid, name]
  );

  const resetForm = () => {
    dispatch({ type: "RESET", payload: buildInitialState(tid, oid, name) });
    setSelectedFeedbackType(null);
  };

  const [state, dispatch] = useReducer<FeedbackState, [FeedbackAction]>(
    feedbackReducer,
    initialState
  );
  const [selectedFeedbackType, setSelectedFeedbackType] = useState<string | null>(null);

  const [discardFeedback, setDiscardFeedback] = useState(false);
  const enableToDiscard = state.comment.length > 0;

  const toggleDiscardFeedback = () => {
    if (!enableToDiscard) {
      hideModalFeedback();
      return;
    }
    setDiscardFeedback((prev) => !prev);
  };

  const hideModalFeedback = () => {
    setIsOpenFeedback(false);
    setTimeout(() => {
      setDiscardFeedback(false);
      resetForm();
    }, 500);
  };

  const { mutate, isPending } = useMutation({
    mutationFn: sendFeedback,
    onSuccess: (data: { success: boolean }) => {
      if (data?.success) {
        hideModalFeedback();
        resetForm();
        onFormSubmit();
      }
    },
    onError: (error) => {
      console.log("Error sending feedback:", error);
    },
  });

  const setFeedbackType = (value: "Like" | "Dislike" | "Idea") => {
    setSelectedFeedbackType(value);
    dispatch({ type: "SET_FEEDBACK_TYPE", payload: value });
  };

  const setComment = (comment: string) => {
    dispatch({ type: "SET_COMMENT", payload: comment });
  };

  const submitFeedback = () => {
    dispatch({ type: "SET_TIMESTAMP", payload: new Date().toISOString() });
  };

  useEffect(() => {
    if (state.timeStamp) {
      mutate(state);
    }
  }, [state.timeStamp, mutate]);

  return {
    state,
    selectedFeedbackType,
    isPending,
    setFeedbackType,
    setComment,
    submitFeedback,
    discardFeedback,
    enableToDiscard,
    toggleDiscardFeedback,
    hideModalFeedback,
  };
};
